import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, X } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { useSite } from "@/context/SiteContext";

export const Journal = () => {
  const { articles } = useSite();
  const [active, setActive] = useState(null);

  return (
    <section id="journal" data-testid="journal-section" className="py-20 sm:py-28 bg-sand2/60 border-y border-ink/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <Eyebrow>Insights & Guides</Eyebrow>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight">The Lembongan journal</h2>
          </div>
          <p className="max-w-sm text-sm text-seastone leading-relaxed">
            Routes, boats and quiet corners — field notes from the island team.
          </p>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-5" data-testid="journal-grid">
          {articles.map((a, i) => (
            <Reveal key={a.id} delay={i * 0.1}>
              <article className="group bg-white rounded-3xl overflow-hidden border border-ink/10 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_rgba(14,27,46,0.12)]" data-testid={`journal-card-${a.id}`}>
                <div className="h-48 overflow-hidden">
                  <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-azure">
                    {a.category} · {a.minutes} min read
                  </p>
                  <h3 className="mt-2 font-serif text-xl leading-snug">{a.title}</h3>
                  <p className="mt-3 text-sm text-seastone leading-relaxed line-clamp-3">{a.excerpt}</p>
                  <button
                    onClick={() => setActive(a)}
                    data-testid={`journal-read-${a.id}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm text-azure hover:text-azure-dark transition-colors"
                  >
                    Read story <ArrowRight size={14} />
                  </button>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-ink/70 backdrop-blur-sm grid place-items-center p-4"
            onClick={() => setActive(null)}
            data-lenis-prevent
            data-testid="journal-modal-overlay"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="bg-sand rounded-3xl max-w-2xl w-full max-h-[85vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="journal-modal"
            >
              <div className="relative">
                <img src={active.image} alt={active.title} className="w-full h-56 sm:h-64 object-cover rounded-t-3xl" />
                <button
                  onClick={() => setActive(null)}
                  data-testid="journal-modal-close"
                  className="absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-full bg-sand/90 text-ink"
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="p-6 sm:p-9">
                <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-azure">
                  {active.category} · {active.minutes} min read
                </p>
                <h3 className="mt-3 font-serif text-2xl sm:text-3xl leading-snug">{active.title}</h3>
                <p className="mt-5 text-base text-ink/80 leading-relaxed">{active.body}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
